import React from 'react'
import FitToPageButton from './FitToPageButton'
import AnimationPropertiesPanel from './AnimationPropertiesPanel'

/**
 * Shape Element Properties Component
 * Handles the properties panel for shape elements
 */
const ShapeElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage
}) => {
  if (element.type !== 'shape') {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Position and Dimensions Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Position & Size</h4>
        <div className="grid grid-cols-2 gap-3">
          {/* X Position */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">X Position</label>
            <input
              type="number"
              value={Math.round(element.x || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'x', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="1"
            />
          </div>

          {/* Y Position */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Y Position</label>
            <input
              type="number"
              value={Math.round(element.y || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'y', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="1"
            />
          </div>

          {/* Width */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Width</label>
            <input
              type="number"
              value={Math.round(element.width || 100)}
              onChange={(e) => updateElementProperty(selectedElement, 'width', Math.max(1, parseFloat(e.target.value) || 100))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              step="1"
            />
          </div>

          {/* Height */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Height</label>
            <input
              type="number"
              value={Math.round(element.height || 100)}
              onChange={(e) => updateElementProperty(selectedElement, 'height', Math.max(1, parseFloat(e.target.value) || 100))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              step="1"
            />
          </div>
        </div>

        {/* Fit to Page Button */}
        <div className="mt-3">
          <FitToPageButton
            selectedElement={selectedElement}
            updateElementProperty={updateElementProperty}
            currentPage={currentPage}
            element={element}
          />
        </div>
      </div>

      {/* Transform Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Transform</h4>
        <div className="grid grid-cols-2 gap-3">
          {/* Rotation */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Rotation (°)</label>
            <input
              type="number"
              value={Math.round(element.rotation || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'rotation', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="1"
              min="-360"
              max="360"
            />
          </div>

          {/* Layer (Z-Index) */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Layer (Z)</label>
            <input
              type="number"
              value={element.layer || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'layer', Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              step="1"
            />
          </div>
        </div>
      </div>

      {/* Shape Properties Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Shape Properties</h4>
        <div className="flex items-center space-x-4 flex-wrap gap-y-2">
          {/* Shape Type */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Shape:</label>
            <select
              value={element.shapeType || 'rectangle'}
              onChange={(e) => updateElementProperty(selectedElement, 'shapeType', e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="rectangle">Rectangle</option>
              <option value="square">Square</option>
              <option value="circle">Circle</option>
              <option value="ellipse">Ellipse</option>
              <option value="triangle">Triangle</option>
              <option value="hexagon">Hexagon</option>
              <option value="star">Star</option>
              <option value="line">Line</option>
            </select>
          </div>

          {/* Fill Color */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Fill:</label>
            <input
              type="color"
              value={element.fill || '#3b82f6'}
              onChange={(e) => updateElementProperty(selectedElement, 'fill', e.target.value)}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            />
            <button
              onClick={() => updateElementProperty(selectedElement, 'fill', 'transparent')}
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear
            </button>
          </div>

          {/* Stroke Color */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Stroke:</label>
            <input
              type="color"
              value={element.stroke && element.stroke !== 'transparent' ? element.stroke : '#000000'}
              onChange={(e) => updateElementProperty(selectedElement, 'stroke', e.target.value)}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            />
            <button
              onClick={() => updateElementProperty(selectedElement, 'stroke', 'transparent')}
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear
            </button>
          </div>

          {/* Stroke Width */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Stroke Width:</label>
            <input
              type="number"
              value={element.strokeWidth ?? 0}
              onChange={(e) => updateElementProperty(selectedElement, 'strokeWidth', Math.max(0, parseInt(e.target.value) || 0))}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="20"
            />
          </div>

          {/* Border Radius (for rectangles) */}
          {(element.shapeType === 'rectangle' || element.shapeType === 'square') && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Radius:</label>
              <input
                type="number"
                value={element.borderRadius || 0}
                onChange={(e) => updateElementProperty(selectedElement, 'borderRadius', parseInt(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
              />
            </div>
          )}

          {/* Opacity */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Opacity:</label>
            <input
              type="range"
              value={element.opacity || 1}
              onChange={(e) => updateElementProperty(selectedElement, 'opacity', parseFloat(e.target.value))}
              className="w-20"
              min="0"
              max="1"
              step="0.1"
            />
            <span className="text-sm text-gray-500 w-8">{Math.round((element.opacity || 1) * 100)}%</span>
          </div>
        </div>
      </div>

      {/* Visibility and Lock Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Visibility & Lock</h4>
        <div className="flex items-center space-x-4">
          {/* Visibility Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`visible-${element.id}`}
              checked={element.visible !== false}
              onChange={(e) => updateElementProperty(selectedElement, 'visible', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={`visible-${element.id}`} className="text-sm text-gray-600">Visible</label>
          </div>

          {/* Lock Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`locked-${element.id}`}
              checked={element.locked || false}
              onChange={(e) => updateElementProperty(selectedElement, 'locked', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={`locked-${element.id}`} className="text-sm text-gray-600">Locked</label>
          </div>
        </div>
      </div>

      {/* Enhanced Shadow */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Drop Shadow</h4>
          <input
            type="checkbox"
            checked={!!(element.shadow?.enabled)}
            onChange={(e) => {
              const shadowConfig = e.target.checked ? {
                enabled: true,
                offsetX: element.shadow?.offsetX || 4,
                offsetY: element.shadow?.offsetY || 4,
                blur: element.shadow?.blur || 8,
                spread: element.shadow?.spread || 0,
                color: element.shadow?.color || '#000000',
                opacity: element.shadow?.opacity || 0.25,
                inset: element.shadow?.inset || false
              } : { enabled: false }
              updateElementProperty(selectedElement, 'shadow', shadowConfig)
            }}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
        </div>

        {element.shadow?.enabled && (
          <div className="space-y-3 pl-4 border-l-2 border-gray-200">
            {/* Position and Size Controls */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Offset X</label>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  value={element.shadow?.offsetX || 4}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    offsetX: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-400">{element.shadow?.offsetX || 4}px</span>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Offset Y</label>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  value={element.shadow?.offsetY || 4}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    offsetY: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-400">{element.shadow?.offsetY || 4}px</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Blur</label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={element.shadow?.blur || 8}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    blur: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-400">{element.shadow?.blur || 8}px</span>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Spread</label>
                <input
                  type="range"
                  min="-20"
                  max="20"
                  value={element.shadow?.spread || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    spread: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-400">{element.shadow?.spread || 0}px</span>
              </div>
            </div>

            {/* Type and Opacity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Type</label>
                <select
                  value={element.shadow?.inset ? 'inset' : 'outset'}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    inset: e.target.value === 'inset'
                  })}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="outset">Drop Shadow</option>
                  <option value="inset">Inner Shadow</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Opacity</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={element.shadow?.opacity || 0.25}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    opacity: parseFloat(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-400">{Math.round((element.shadow?.opacity || 0.25) * 100)}%</span>
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Shadow Color</label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={element.shadow?.color || '#000000'}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    color: e.target.value
                  })}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={element.shadow?.color || '#000000'}
                  onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                    ...element.shadow,
                    color: e.target.value
                  })}
                  className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Animation Properties */}
      <AnimationPropertiesPanel
        element={element}
        selectedElement={selectedElement}
        updateElementProperty={updateElementProperty}
      />
    </div>
  )
}

export default ShapeElementProperties
